import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AuthModal from '../pages/login/AuthModal';
import { homePathForRole, isAdminRole } from '../services/userProfile';

const AuthModalContext = createContext({
  openAuthModal: () => {},
  closeAuthModal: () => {},
});

export function useAuthModal() {
  return useContext(AuthModalContext);
}

function isStaffRole(role) {
  return isAdminRole(role) || role === 'partner' || role === 'gameMaker';
}

/**
 * Global Sign in / Sign up modal. Call openAuthModal({ mode, onSuccess }).
 * Deep links: /login and /signup redirect to /?auth=signin|signup; this provider
 * opens the modal and strips the query.
 */
export function AuthModalProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('signin');
  const onSuccessRef = useRef(null);

  const closeAuthModal = useCallback(() => {
    setOpen(false);
    onSuccessRef.current = null;
  }, []);

  const openAuthModal = useCallback((options = {}) => {
    const nextMode = options.mode === 'signup' ? 'signup' : 'signin';
    onSuccessRef.current =
      typeof options.onSuccess === 'function' ? options.onSuccess : null;
    setMode(nextMode);
    setOpen(true);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const auth = params.get('auth');
    if (auth !== 'signin' && auth !== 'signup') return undefined;

    openAuthModal({
      mode: auth,
      onSuccess: (_uid, profile) => {
        const role = profile?.role;
        if (isStaffRole(role)) {
          navigate(homePathForRole(role), { replace: true });
        }
      },
    });

    params.delete('auth');
    const nextSearch = params.toString();
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : '',
        hash: location.hash,
      },
      { replace: true }
    );
    return undefined;
  }, [
    location.hash,
    location.pathname,
    location.search,
    navigate,
    openAuthModal,
  ]);

  const handleSuccess = useCallback(async (uid, profile) => {
    const cb = onSuccessRef.current;
    onSuccessRef.current = null;
    if (typeof cb === 'function') {
      await cb(uid, profile);
    }
  }, []);

  const value = useMemo(
    () => ({ openAuthModal, closeAuthModal }),
    [openAuthModal, closeAuthModal]
  );

  return (
    <AuthModalContext.Provider value={value}>
      {children}
      <AuthModal
        open={open}
        initialMode={mode}
        onClose={closeAuthModal}
        onSuccess={handleSuccess}
      />
    </AuthModalContext.Provider>
  );
}
